import { CdkScrollable } from '@angular/cdk/scrolling';
import { inject, signal, untracked, effect, isSignal, computed } from '@angular/core';
import { Router, Scroll } from '@angular/router';
import { filter, debounceTime, first, fromEvent, Subscription, takeUntil, timer } from 'rxjs';
import { NavigationStore } from '../store';
import { AnchorNavItem, NavItemType } from '../types';
import { ACTIVE_SECTION_STRATEGY, ActiveSectionCandidate } from './active-section-strategy';
import { ScrollNavigationAnchor } from './ScrollNavigationAnchor';

/** Page order. Layout-independent, so it is right even while sections are still empty or detached. */
const compareAnchors = (a: ScrollNavigationAnchor, b: ScrollNavigationAnchor) => {
  const position = a.element.compareDocumentPosition(b.element);
  if (position & Node.DOCUMENT_POSITION_FOLLOWING) {
    return -1;
  }
  if (position & Node.DOCUMENT_POSITION_PRECEDING) {
    return 1;
  }
  return 0;
};

/** Keys that scroll the page when nothing editable has focus. */
const SCROLL_KEYS = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ']);

/** How long the page has to stop changing size before a restore is considered landed. */
const RESTORE_SETTLE_MS = 1200;

const isEditable = (target: EventTarget | null) => {
  const el = target as HTMLElement | null;
  return !!el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));
};

export class ScrollNavigationManager {
  private readonly _navStore = inject(NavigationStore);
  private readonly _router = inject(Router);
  private readonly _strategy = inject(ACTIVE_SECTION_STRATEGY);
  private readonly _anchors: Map<string, ScrollNavigationAnchor> = new Map();
  private readonly _anchorHashToId: Map<string, string> = new Map();
  private _anchorIds: string[] = [];
  private readonly _sortedKey = signal<string>('');

  /**
   * The anchor the page is scrolled to, as decided by the active-section strategy; null above the
   * first section. Drives both the URL hash and the navigation highlight — one source, so they can
   * never disagree.
   */
  private readonly _activeId = signal<string | null>(null);
  /** Bumped when a reason to withhold the hash goes away, so the effect below re-evaluates. */
  private readonly _hashTick = signal(0);
  private _computeScheduled = false;

  /**
   * True only while one of our own scrolls is in flight, so the sections it sweeps past don't get
   * written into the hash. Cleared when that scroll ends — or at once on user input, because from
   * then on the hash has to follow the user, not us. It used to start `true` and clear only after a
   * 100ms gap in scroll events, which meant a user who kept the wheel moving got no hash updates at
   * all until they paused.
   */
  private readonly _ignoreScroll = signal(false);
  private _endProgrammaticScroll?: () => void;

  /**
   * A hash we were asked to scroll to whose anchor had not registered yet (sections are injected
   * as their data arrives, so on a fresh load the target usually isn't there when the router asks).
   * Honoured when the anchor registers; dropped on the first user input, because a restore that
   * fires after the user has started scrolling just yanks them away from what they were reading.
   */
  private _pendingHash: string | null = null;

  /**
   * A restore in progress. Sections arrive with their data, so the first scroll usually happens
   * while the page is still too short to scroll, and content keeps landing above the target for a
   * while after; the target is re-aligned on every size change until the page goes quiet, then
   * normal tracking resumes.
   */
  private _restore: { hash: string; stop: () => void } | null = null;

  /**
   * What the user is looking at: the section whose box contains the viewport's top edge, and where
   * that edge sat relative to the scroller. Re-read on every scroll.
   *
   * Browsers do this compensation themselves (scroll anchoring), but on this page their anchor
   * selection keeps landing on a box that does not move — depending on layout and position it
   * compensates for nothing — so the residual is measured after layout and corrected here.
   * Whatever the browser already adjusted reads as zero, so this never double-adjusts.
   */
  private _viewRef: { element: HTMLElement; top: number } | null = null;
  private readonly _stabilizer: ResizeObserver;

  constructor(public readonly container: CdkScrollable) {
    const element = container.getElementRef().nativeElement;

    // User input is intent. Whatever we had planned for the viewport yields to it.
    const onUserInput = () => {
      this._restore?.stop();
      this._pendingHash = null;
      this.endProgrammaticScroll();
    };

    element.addEventListener(
      'scroll',
      () => {
        this.snapshotView();
        this.scheduleCompute();
      },
      { passive: true },
    );
    this._stabilizer = new ResizeObserver(() => {
      const ref = this._viewRef;
      if (ref && !untracked(this._ignoreScroll) && element.scrollTop > 0 && ref.element.isConnected) {
        const residual = ref.element.getBoundingClientRect().top - element.getBoundingClientRect().top - ref.top;
        if (Math.abs(residual) >= 1) {
          element.scrollTop += residual;
        }
      }
      this.computeActive();
    });
    for (const type of ['wheel', 'touchmove', 'pointerdown']) {
      element.addEventListener(type, onUserInput, { passive: true, capture: true });
    }
    document.addEventListener(
      'keydown',
      (e: KeyboardEvent) => {
        if (SCROLL_KEYS.has(e.key) && !isEditable(e.target)) {
          onUserInput();
        }
      },
      { capture: true },
    );

    // Every navigation is fresh intent: honour its fragment now, or when the anchor registers.
    // The fragment is taken from the event, synchronously: by the time a deferred read of
    // location.hash would run, the hash-writing effect below may already have replaced it with
    // whatever section happens to be active at scrollTop 0.
    //
    // This manager is created lazily, by the first anchor that registers — which on a fresh load
    // is after the router has already emitted the Scroll event for the navigation that brought us
    // here. So the navigation we are born into is read off the router directly.
    const initialHash = this._router.parseUrl(this._router.url).fragment ?? '';
    if (initialHash) {
      this._pendingHash = initialHash;
      setTimeout(() => this.scrollToHash(initialHash), 0);
    }
    this._router.events.pipe(filter((e): e is Scroll => e instanceof Scroll)).subscribe((e) => {
      const hash = e.anchor ?? '';
      this._restore?.stop();
      this._pendingHash = hash;
      setTimeout(() => this.scrollToHash(hash), 0);
    });

    effect(() => {
      const activeId = this._activeId();
      this._hashTick();
      // While one of our scrolls is in flight, or a restore is outstanding, the URL keeps the
      // fragment it was given; the first user input drops the restore and the hash follows the
      // viewport from then on.
      if (untracked(this._ignoreScroll) || this._pendingHash !== null || activeId === null) {
        return;
      }
      const navItem = this._navStore.getById(activeId) as AnchorNavItem | undefined;
      if (!navItem) {
        return;
      }
      const hidden = isSignal(navItem.hidden) ? untracked(navItem.hidden) : false;
      const disabled = isSignal(navItem.disabled) ? untracked(navItem.disabled) : false;
      const ank = isSignal(navItem.anchor) ? untracked(navItem.anchor) : undefined;
      if (hidden || disabled || ank === undefined) {
        return;
      }
      if (location.hash.replace('#', '') !== ank) {
        const currentUrlWithoutHash = location.pathname + location.search;
        history.replaceState(null, '', `${currentUrlWithoutHash}${ank.trim() ? '#' + ank : ''}`);
      }
    });
  }

  private scheduleCompute() {
    if (this._computeScheduled) {
      return;
    }
    this._computeScheduled = true;
    requestAnimationFrame(() => {
      this._computeScheduled = false;
      this.computeActive();
    });
  }

  /**
   * Asks the strategy which section is active for the current scroll position. Candidates are the
   * sections that are on the page, have height, and have a visible, enabled nav item; the page's
   * top anchor (empty hash) is not a candidate but is what "nothing applies" resolves to.
   */
  private computeActive() {
    this.sortAnchors();
    const scroller = this.container.getElementRef().nativeElement;
    const origin = scroller.getBoundingClientRect().top;
    const sections: ActiveSectionCandidate[] = [];
    let topAnchorId: string | null = null;
    for (const id of this._anchorIds) {
      const anchor = this._anchors.get(id)!;
      if (id.split('/').pop() === '') {
        topAnchorId = id;
        continue;
      }
      if (!anchor.element.isConnected || untracked(anchor.navHidden) || untracked(anchor.navDisabled)) {
        continue;
      }
      const rect = anchor.element.getBoundingClientRect();
      if (rect.height <= 0) {
        continue;
      }
      sections.push({ id, top: rect.top - origin, bottom: rect.bottom - origin });
    }
    const active = this._strategy({
      sections,
      viewportHeight: scroller.clientHeight,
      scrollTop: scroller.scrollTop,
      scrollMax: scroller.scrollHeight - scroller.clientHeight,
      previous: untracked(this._activeId),
    });
    this._activeId.set(active ?? topAnchorId);
  }

  private scrollToHash(hash: string) {
    if (this._pendingHash !== hash) {
      // user input (or a later navigation) superseded this request while it was deferred
      return;
    }
    if (hash === '') {
      this._restore?.stop();
      this._pendingHash = null;
      this.beginProgrammaticScroll();
      this.container.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    const id = this._anchorHashToId.get(hash);
    const anchor = id !== undefined ? this._anchors.get(id) : undefined;
    if (!anchor) {
      this._pendingHash = hash;
      return;
    }
    this.scrollToAnchor(anchor, 'smooth');
  }

  private scrollToAnchor(anchor: ScrollNavigationAnchor, behavior: ScrollBehavior) {
    this.beginProgrammaticScroll();
    anchor.element.scrollIntoView({ behavior });
    this.trackRestore(anchor);
  }

  /**
   * Keeps re-aligning `anchor` while the page is still changing size, then releases the pending
   * hash. Stopped early by user input, by the next navigation, or by the anchor going away.
   */
  private trackRestore(anchor: ScrollNavigationAnchor) {
    this._restore?.stop();
    const hash = this._pendingHash;
    if (hash === null) {
      return;
    }
    const scroller = this.container.getElementRef().nativeElement;
    let quiet: number | undefined;
    const observer = new ResizeObserver(() => {
      if (this._restore?.hash !== hash) {
        return;
      }
      this.beginProgrammaticScroll();
      anchor.element.scrollIntoView({ behavior: 'instant' });
      armQuiet();
    });
    const stop = () => {
      clearTimeout(quiet);
      observer.disconnect();
      if (this._restore?.hash === hash) {
        this._restore = null;
        if (this._pendingHash === hash) {
          this._pendingHash = null;
          this._hashTick.update((n) => n + 1);
        }
      }
    };
    const armQuiet = () => {
      clearTimeout(quiet);
      quiet = window.setTimeout(() => {
        stop();
      }, RESTORE_SETTLE_MS);
    };
    this._restore = { hash, stop };
    for (const child of Array.from(scroller.children)) {
      observer.observe(child);
    }
    observer.observe(anchor.element);
    armQuiet();
  }

  /**
   * Marks a scroll of ours as in flight until it ends. "Ends" is `scrollend` where the browser has
   * it, otherwise the first quiet gap after it started; and if nothing scrolls at all within 250ms
   * (we were already there) there is nothing to wait for.
   */
  private beginProgrammaticScroll() {
    this.endProgrammaticScroll();
    this._ignoreScroll.set(true);
    const element = this.container.getElementRef().nativeElement;
    const scrolled$ = this.container.elementScrolled();
    const end$ = 'onscrollend' in window ? fromEvent(element, 'scrollend').pipe(first()) : scrolled$.pipe(debounceTime(120), first());
    const subscription = new Subscription();
    subscription.add(end$.subscribe(() => this.endProgrammaticScroll()));
    subscription.add(
      timer(250)
        .pipe(takeUntil(scrolled$))
        .subscribe(() => this.endProgrammaticScroll()),
    );
    this._endProgrammaticScroll = () => subscription.unsubscribe();
  }

  private endProgrammaticScroll() {
    this._endProgrammaticScroll?.();
    this._endProgrammaticScroll = undefined;
    this._ignoreScroll.set(false);
    this.snapshotView();
    this.computeActive();
    this._hashTick.update((n) => n + 1);
  }

  /**
   * Records which section the user is on and where its top edge is; no-op mid programmatic scroll.
   * "On" means the section's box contains the viewport's top edge. If no section does — the
   * viewport is above them all, in the product card — there is no reference and nothing is
   * compensated: anchoring to the first section *below* would drag the view along whenever
   * something between the two grew.
   */
  private snapshotView() {
    if (untracked(this._ignoreScroll)) {
      return;
    }
    const scroller = this.container.getElementRef().nativeElement;
    const origin = scroller.getBoundingClientRect().top;
    this._viewRef = null;
    for (const id of this._anchorIds) {
      const el = this._anchors.get(id)?.element;
      if (!el) {
        continue;
      }
      const rect = el.getBoundingClientRect();
      const top = rect.top - origin;
      if (rect.height > 0 && top <= 0 && rect.bottom - origin > 0) {
        this._viewRef = { element: el, top };
        return;
      }
    }
  }

  private findId(anchor: ScrollNavigationAnchor) {
    return this._anchors.entries().find(([_, a]) => a === anchor)?.[0];
  }

  public addAnchor(anchorInst: ScrollNavigationAnchor) {
    const id = untracked(anchorInst.id)!;
    const existingId = this.findId(anchorInst);
    if (existingId !== undefined && existingId !== id) {
      this.removeAnchor(anchorInst);
    }
    if (existingId !== id) {
      this._anchors.set(id, anchorInst);
      this._anchorIds.push(id);

      const anchor = id.split('/').pop()!;
      this._anchorHashToId.set(anchor, id);

      const orderedPriority = computed(() => {
        this._sortedKey();
        if (anchor === '') {
          return Number.MAX_SAFE_INTEGER;
        }
        const ids = [...this._anchorIds].reverse();
        return ids.indexOf(id);
      });

      this._navStore.remove(id);
      this._navStore.add({
        id,
        type: NavItemType.Anchor,
        label: anchorInst.navLabel,
        relativeTo: anchorInst.relativeTo,
        anchor: signal(anchor),
        disabled: anchorInst.navDisabled,
        heading: anchorInst.navHeading,
        hidden: anchorInst.navHidden,
        icon: anchorInst.navIcon,
        priority: computed(() => anchorInst.navPriority() ?? orderedPriority()),
        active: computed(() => this._activeId() === id),
      } as AnchorNavItem);

      this.sortAnchors();
      // The section registers from inside the effect that creates it, before the render target has
      // moved its node into place; evaluate once the layout that follows has happened.
      this.scheduleCompute();

      // Watch this section and the page it lives in for size changes.
      this._stabilizer.observe(anchorInst.element);
      for (const child of Array.from(this.container.getElementRef().nativeElement.children)) {
        this._stabilizer.observe(child);
      }

      if (this._pendingHash === anchor) {
        // Same wait as above. Instant rather than smooth: a smooth scroll aims at an offset computed
        // when it starts, and content is still arriving above this section — the restore keeps
        // re-aligning it until the page settles.
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            if (this._pendingHash === anchor && this._anchors.get(id) === anchorInst) {
              this.scrollToAnchor(anchorInst, 'instant');
            }
          }),
        );
      }
    }
  }

  public removeAnchor(anchor: ScrollNavigationAnchor) {
    const id = this.findId(anchor);
    if (id !== undefined) {
      const restore = this._restore;
      if (restore && restore.hash === id.split('/').pop()) {
        restore.stop();
      }
      this._stabilizer.unobserve(anchor.element);
      this._anchors.delete(id);
      this._anchorIds = this._anchorIds.filter((i) => i !== id);
      this._navStore.remove(id);
      this._anchorHashToId.delete(id.split('/').pop()!);
      this.sortAnchors();
      if (untracked(this._activeId) === id) {
        this.computeActive();
      }
    }
  }

  private sortAnchors() {
    this._anchorIds.sort((a, b) => compareAnchors(this._anchors.get(a)!, this._anchors.get(b)!));
    this._sortedKey.set(this._anchorIds.join('|'));
  }
}
